import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircleQuestion, Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ASK_UI as UI } from "@/data/askI18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AskResult } from "@/components/ask/AskResult";
import { logWarn } from "@/lib/log";

const EXAMPLES = ["example1", "example2", "example3"];

// "Tanya Manasik": free-text question → Jev topic routing (backend POST /ask) → pages + FAQ answer.
export const AskManasik = ({ compact = false }) => {
  const { tc, lang } = useLang();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [params, setParams] = useSearchParams();
  const autoAsked = useRef(false);

  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (question.length < 3) { toast(tc(UI.tooShort)); return; }
    setQ(question); setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ask", { question, lang });
      setResult({ ...data, question });
    } catch (e) {
      logWarn("ask failed:", e);
      const status = e.response?.status;
      toast.error(tc(status === 429 || status === 503 ? UI.busy : UI.failed));
    } finally { setBusy(false); }
  };

  // Deep link from quick search: /?ask=<question> runs once, then the param is cleared.
  useEffect(() => {
    const initial = params.get("ask");
    if (!initial || autoAsked.current) return;
    autoAsked.current = true;
    setParams({}, { replace: true });
    ask(initial);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="rounded-3xl border-primary/20 bg-primary/[0.04] shadow-sm p-5 sm:p-6 space-y-4" data-testid="ask-manasik">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-white"><MessageCircleQuestion className="h-5 w-5" /></span>
          <div className="min-w-0">
            <p className="font-serif text-xl leading-tight">{tc(UI.title)}</p>
            {!compact && <p className="text-xs text-muted-foreground mt-0.5">{tc(UI.subtitle)}</p>}
          </div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tc(UI.placeholder)} maxLength={300}
            data-testid="ask-input" className="h-12 rounded-xl bg-background" />
          <Button type="submit" disabled={busy} data-testid="ask-submit" className="h-12 rounded-xl px-4 shrink-0">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4 me-2 rtl:-scale-x-100" />{tc(UI.ask)}</>}
          </Button>
        </form>
        {busy && <p className="text-xs text-muted-foreground" data-testid="ask-thinking">{tc(UI.thinking)}</p>}
        {!result && !busy && (
          <div className="flex flex-wrap items-center gap-2" data-testid="ask-examples">
            <span className="text-xs text-muted-foreground">{tc(UI.examplesLabel)}</span>
            {EXAMPLES.map((k) => (
              <button key={k} type="button" onClick={() => ask(tc(UI[k]))} data-testid={`ask-${k}`}
                className="inline-flex min-h-9 items-center rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors">
                {tc(UI[k])}
              </button>
            ))}
          </div>
        )}
        {result && <AskResult result={result} />}
        <p className="flex items-center gap-1 text-[11px] text-muted-foreground/70"><Sparkles className="h-3 w-3" />{tc(UI.poweredBy)}</p>
      </Card>
    </motion.div>
  );
};
